import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';

// Applicant-side steps — each card gets its own light tint. Text is [en, ta].
const steps = [
  {
    title: ['Register or Login', 'பதிவு செய்தல் / உள்நுழைதல்'],
    lines: [
      ['Existing users: login with your credentials', 'ஏற்கனவே பதிவு செய்தவர்கள்: உங்கள் பயனர் விவரங்களுடன் உள்நுழையவும்'],
      ['New organizations / individuals: register with your details', 'புதிய அமைப்புகள் / தனிநபர்கள்: உங்கள் விவரங்களுடன் பதிவு செய்யவும்'],
      ['Instant access after registration', 'பதிவு செய்தவுடன் உடனடி அணுகல்'],
    ],
    tint: 'bg-sky-50 border-sky-200',
    num: 'bg-sky-600',
    register: true,
  },
  {
    title: ['Choose Your Water Body', 'நீர்நிலையைத் தேர்வு செய்தல்'],
    lines: [
      ['Browse water bodies across Tamil Nadu', 'தமிழ்நாடு முழுவதும் உள்ள நீர்நிலைகளைப் பார்வையிடவும்'],
      ['Filter by department, district and block / ULB', 'துறை, மாவட்டம் மற்றும் வட்டாரம் / உள்ளாட்சி வாரியாக வடிகட்டவும்'],
      ['Select the water body for your project', 'உங்கள் திட்டத்திற்கான நீர்நிலையைத் தேர்வு செய்யவும்'],
    ],
    tint: 'bg-amber-50 border-amber-200',
    num: 'bg-amber-500',
  },
  {
    title: ['Fill Application', 'விண்ணப்பத்தை நிரப்புதல்'],
    lines: [
      ['Choose restoration activities', 'புனரமைப்புப் பணிகளைத் தேர்வு செய்யவும்'],
      ['Enter estimated cost and upload documents', 'மதிப்பீட்டுத் தொகையை உள்ளிட்டு ஆவணங்களைப் பதிவேற்றவும்'],
      ['Accept self-declaration — save as draft anytime', 'சுய உறுதிமொழியை ஏற்கவும் — எப்போது வேண்டுமானாலும் வரைவாகச் சேமிக்கலாம்'],
    ],
    tint: 'bg-violet-50 border-violet-200',
    num: 'bg-violet-600',
  },
  {
    title: ['Submit & Track', 'சமர்ப்பித்தல் மற்றும் கண்காணித்தல்'],
    lines: [
      ['Submit to the department that owns the water body', 'நீர்நிலைக்குப் பொறுப்பான துறைக்குச் சமர்ப்பிக்கவும்'],
      ['Officers are notified by email', 'அலுவலர்களுக்கு மின்னஞ்சல் மூலம் தெரிவிக்கப்படும்'],
      ['Track status on your dashboard', 'உங்கள் டாஷ்போர்டில் விண்ணப்ப நிலையைக் கண்காணிக்கவும்'],
    ],
    tint: 'bg-emerald-50 border-emerald-200',
    num: 'bg-emerald-600',
  },
];

const reviews = [
  {
    level: 'L1',
    title: [
      'L1 Officer — Technical Review (in consultation with L1A officer, if applicable)',
      'நிலை 1 அலுவலர் — தொழில்நுட்ப ஆய்வு (தேவைப்படின் L1A அலுவலருடன் கலந்தாலோசித்து)',
    ],
    text: [
      'Field-level officer of the department verifies the water body, activities and documents.',
      'துறையின் களநிலை அலுவலர் நீர்நிலை, பணிகள் மற்றும் ஆவணங்களைச் சரிபார்ப்பார்.',
    ],
    head: 'bg-brand-600',
  },
  {
    level: 'L2',
    title: ['L2 Officer — Approval & NOC Issue', 'நிலை 2 அலுவலர் — ஒப்புதல் மற்றும் தடையின்மைச் சான்று வழங்குதல்'],
    text: [
      'Approving authority reviews the L1 recommendation and issues the NOC.',
      'ஒப்புதல் அளிக்கும் அலுவலர் நிலை 1 பரிந்துரையை ஆய்வு செய்து தடையின்மைச் சான்றை வழங்குவார்.',
    ],
    head: 'bg-brand-800',
  },
];

const actions = [
  { label: ['Approve', 'ஒப்புதல்'], desc: ['Moves to the next level / NOC issued', 'அடுத்த நிலைக்குச் செல்லும் / தடையின்மைச் சான்று வழங்கப்படும்'], cls: 'bg-emerald-100 text-emerald-800 border-emerald-600' },
  { label: ['Return', 'திருப்பி அனுப்புதல்'], desc: ['Sent back for clarification — resubmit to continue', 'விளக்கத்திற்காகத் திருப்பி அனுப்பப்படும் — மீண்டும் சமர்ப்பித்துத் தொடரலாம்'], cls: 'bg-amber-100 text-amber-800 border-amber-500' },
  { label: ['Reject', 'நிராகரித்தல்'], desc: ['Application closed with reasons', 'காரணங்களுடன் விண்ணப்பம் முடிக்கப்படும்'], cls: 'bg-rose-100 text-rose-800 border-rose-600' },
];

const afterNoc = [
  {
    icon: '📷',
    title: ['Execute Work & Upload Photos', 'பணிகளை மேற்கொண்டு புகைப்படங்களைப் பதிவேற்றுதல்'],
    text: ['The organization carries out the restoration and uploads stage-wise photos', 'அமைப்பு புனரமைப்புப் பணிகளை மேற்கொண்டு, ஒவ்வொரு நிலையிலும் புகைப்படங்களைப் பதிவேற்றும்'],
    tint: 'bg-sky-50 border-sky-200',
  },
  {
    icon: '📝',
    title: ['Request Completion Certificate', 'பணி நிறைவுச் சான்று கோருதல்'],
    text: ['After the works are finished, the organization requests the completion certificate', 'பணிகள் முடிந்தவுடன், அமைப்பு பணி நிறைவுச் சான்றைக் கோரும்'],
    tint: 'bg-amber-50 border-amber-200',
  },
  {
    icon: '✅',
    title: ['Completion Certificate', 'பணி நிறைவுச் சான்று'],
    text: ['Issued once the L2 officer verifies the work and approves the request', 'நிலை 2 அலுவலர் பணியைச் சரிபார்த்து கோரிக்கைக்கு ஒப்புதல் அளித்தவுடன் வழங்கப்படும்'],
    tint: 'bg-emerald-50 border-emerald-200',
  },
];

const Arrow = () => (
  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const SectionTitle = ({ eyebrow, title }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold uppercase tracking-widest text-brand-600">{eyebrow}</span>
    <h2 className="mt-1 text-2xl font-semibold text-slate-900">{title}</h2>
    <div className="w-12 h-1 bg-brand-accent mt-3"></div>
  </div>
);

const ApplicationWorkflow = () => {
  const { tr } = useLang();
  const t = (pair) => tr(pair[0], pair[1]);

  return (
    <div className="pt-20 bg-slate-100 min-h-screen">
      {/* Page header */}
      <div className="relative bg-cover bg-center border-b-4 border-brand-accent" style={{ backgroundImage: "url('/images/water3.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-700/95 via-brand-600/85 to-teal-500/70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <h1 className="text-3xl lg:text-4xl font-semibold text-white">
            {tr('Application Workflow', 'விண்ணப்பச் செயல்முறை')}
          </h1>
          <p className="mt-2 text-white/90 max-w-2xl">
            {tr(
              'Simple steps to get your water body restoration project approved — from registration to completion certificate.',
              'பதிவு செய்வது முதல் பணி நிறைவுச் சான்று பெறுவது வரை — உங்கள் நீர்நிலைப் புனரமைப்புத் திட்டத்திற்கு ஒப்புதல் பெறுவதற்கான எளிய படிநிலைகள்.'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-10">
        {/* Applicant steps */}
        <section>
          <SectionTitle eyebrow={tr('For Applicants', 'விண்ணப்பதாரர்களுக்கு')} title={tr('How to Apply', 'விண்ணப்பிக்கும் முறை')} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, i) => (
              <div key={step.title[0]} className={`border ${step.tint} p-5 relative flex flex-col`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-9 h-9 flex-shrink-0 ${step.num} text-white font-semibold flex items-center justify-center`}>
                    {i + 1}
                  </span>
                  <h3 className="font-semibold text-slate-900">{t(step.title)}</h3>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-600 flex-1">
                  {step.lines.map((l) => (
                    <li key={l[0]} className="flex gap-2">
                      <span className="text-slate-400">•</span>
                      <span>{t(l)}</span>
                    </li>
                  ))}
                </ul>
                {step.register && (
                  <Link
                    to="/register"
                    className="mt-4 inline-block text-center px-4 py-2 text-sm font-semibold bg-brand-700 text-white hover:bg-brand-800"
                  >
                    {tr('Register Now for NOC', 'தடையின்மைச் சான்றுக்கு இப்போதே பதிவு செய்க')}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Department review */}
        <section className="bg-white border border-slate-200 p-6 lg:p-8">
          <SectionTitle eyebrow={tr('Department Review', 'துறை ஆய்வு')} title={tr('Two-Level Approval', 'இரு நிலை ஒப்புதல்')} />
          <p className="text-sm text-slate-600 -mt-2 mb-6">
            {tr(
              'Every application is reviewed by the department that owns the selected water body.',
              'ஒவ்வொரு விண்ணப்பமும் தேர்வு செய்யப்பட்ட நீர்நிலைக்குப் பொறுப்பான துறையால் ஆய்வு செய்யப்படும்.'
            )}
          </p>

          <div className="flex flex-col lg:flex-row items-stretch gap-4">
            {reviews.map((r, i) => (
              <React.Fragment key={r.level}>
                <div className="flex-1 border border-slate-200">
                  <div className={`${r.head} px-5 py-3 flex items-center justify-between gap-3`}>
                    <span className="text-white font-semibold">{t(r.title)}</span>
                    <span className="text-xs font-bold bg-white/20 text-white px-2 py-0.5">{r.level}</span>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-slate-600">{t(r.text)}</p>
                  </div>
                </div>
                {i < reviews.length - 1 && (
                  <div className="flex items-center justify-center rotate-90 lg:rotate-0">
                    <Arrow />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Officer actions */}
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {actions.map((a) => (
              <div key={a.label[0]} className={`border-l-4 ${a.cls} px-4 py-3`}>
                <div className="font-semibold text-sm">{t(a.label)}</div>
                <div className="text-xs mt-0.5 opacity-90">{t(a.desc)}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-brand-800 text-white px-5 py-4">
            <span className="text-sm text-brand-100">
              {tr(
                'NOC issued in Tamil & English — download from your dashboard',
                'தடையின்மைச் சான்று தமிழ் மற்றும் ஆங்கிலத்தில் வழங்கப்படும் — உங்கள் டாஷ்போர்டில் பதிவிறக்கம் செய்யலாம்'
              )}
            </span>
          </div>
        </section>

        {/* After NOC */}
        <section>
          <SectionTitle eyebrow={tr('After Approval', 'ஒப்புதலுக்குப் பின்')} title={tr('Execution & Completion', 'பணி செயலாக்கம் மற்றும் நிறைவு')} />
          <div className="grid md:grid-cols-3 gap-4">
            {afterNoc.map((item) => (
              <div key={item.title[0]} className={`border ${item.tint} p-5 flex items-start gap-4`}>
                <span className="text-3xl">{item.icon}</span>
                <div>
                  <p className="font-semibold text-slate-900">{t(item.title)}</p>
                  <p className="text-sm text-slate-600 mt-1">{t(item.text)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white border border-slate-200 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {tr('Ready to restore a water body?', 'நீர்நிலையைப் புனரமைக்கத் தயாரா?')}
            </h3>
            <p className="text-sm text-slate-600">
              {tr(
                'Register your Organization or login to start a new NOC application.',
                'புதிய தடையின்மைச் சான்று விண்ணப்பத்தைத் தொடங்க உங்கள் அமைப்பைப் பதிவு செய்யவும் அல்லது உள்நுழையவும்.'
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/register" className="px-5 py-2.5 text-sm font-semibold border border-brand-700 text-brand-700 hover:bg-brand-50">
              {tr('Register Now for NOC', 'இப்போதே பதிவு செய்க')}
            </Link>
            <Link to="/login" className="px-5 py-2.5 text-sm font-semibold bg-brand-700 text-white hover:bg-brand-800">
              {tr('Login', 'உள்நுழைய')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ApplicationWorkflow;
